import { useMemo, useState } from 'react';
import TotalProduction from './TotalProduction/TotalProduction';
import AppointmentsRequested from './AppointmentsRequested/AppointmentsRequested';
import StatCards from './StatCards/StatCards';
import PatientsCard from './PatientsCard/PatientsCard';
import AgeOfPatients from './AgeOfPatients/AgeOfPatients';
import PreferredPractitioner from './PreferredPractitioner/PreferredPractitioner';
import AppointmentMetricBreakdown from './AppointmentMetricBreakdown/AppointmentMetricBreakdown';
import PractitionerPerformance from './PractitionerPerformance/PractitionerPerformance';
import ServicePerformance from './ServicePerformance/ServicePerformance';
import TimeOfRequests from './TimeOfRequests/TimeOfRequests';
import AppointmentDateRange from 'components/AppointmentDateRange/AppointmentDateRange';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useAppointmentDateRange from 'hooks/useAppointmentDateRange';
import { loadSelectedClinicId } from 'utils/storage';
import { CALENDAR_TYPE } from 'utils/getDateRangeByCalendarType';
import HourTypeDropdown, {
  HOUR_TYPE_OPTIONS,
} from './HourTypeDropdown/HourTypeDropdown';
import moment from 'moment';
import RequestsScheduleOverTime from './RequestsScheduleOverTime/RequestsScheduleOverTime';
import useClinic from 'hooks/useClinic';
import { useUser } from 'context/userContext';
import ClinicDropdown from './ClinicDropdown/ClinicDropdown';
import TrafficBySource from './TrafficBySource/TrafficBySource';
import { ROLE } from 'utils/constants';

const OnlineSchedulingReportPage = () => {
  const selectedClinicId = loadSelectedClinicId();
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([
    selectedClinicId,
  ]);
  const [selectedCalendarType, setSelectedCalendarType] = useState(
    CALENDAR_TYPE.ALL_TIME
  );

  const [selectedDateRange, setSelectedDateRange] = useState<{
    fromDate: string;
    toDate: string;
  } | null>(null);

  const [selectedHourType, setSelectedHourType] = useState(
    HOUR_TYPE_OPTIONS[0]
  );

  const {
    data: allTimeAppointmentDateRange,
    isLoading: isLoadingAllTimeDateRange,
  } = useAppointmentDateRange({ clinicIds: selectedClinicIds });

  const { data: clinic } = useClinic();

  const {
    user: { adminRole, clinicUserRoles },
  } = useUser();

  const isClinicDropdownVisible =
    adminRole?.role === ROLE.GLOBAL_ADMIN ||
    adminRole?.role === ROLE.GROUP_ADMIN;

  const earliestDateTime = allTimeAppointmentDateRange?.earliestDateTime;
  const latestDateTime = allTimeAppointmentDateRange?.latestDateTime;

  const allTimeDateRange = useMemo(() => {
    if (!earliestDateTime || !latestDateTime) {
      return null;
    }

    return {
      fromDate: moment(earliestDateTime).startOf('day').utc(true).format(),
      toDate: moment(latestDateTime).endOf('day').utc(true).format(),
    };
  }, [earliestDateTime, latestDateTime]);

  const onChangeDateRange = (params: { fromDate: string; toDate: string }) => {
    setSelectedDateRange({
      fromDate: moment(params.fromDate).startOf('day').utc(true).format(),
      toDate: moment(params.toDate).endOf('day').utc(true).format(),
    });
  };

  const dateRangeForChildren =
    selectedCalendarType === CALENDAR_TYPE.ALL_TIME
      ? (allTimeDateRange ?? selectedDateRange ?? { fromDate: '', toDate: '' })
      : (selectedDateRange ?? { fromDate: '', toDate: '' });

  const isWaitingForInitialRange =
    isLoadingAllTimeDateRange || (!!selectedClinicId && !clinic);

  return (
    <div
      className="pt-2.4 pl-1.8 pr-4 text-14 leading-[2.1rem] text-darkest-grey pb-12 relative"
      data-testid="page-online-scheduling-report"
    >
      <div className="px-3.2 flex flex-row justify-between items-center">
        <div className="flex items-center gap-x-1.6">
          <h3 className="text-24 font-bold leading-[3.6rem]">
            Online Scheduling Report
          </h3>
          {isClinicDropdownVisible && (
            <ClinicDropdown
              clinicUserRoles={clinicUserRoles}
              initialClinicId={selectedClinicId}
              onSelect={setSelectedClinicIds}
            />
          )}
        </div>

        <div className="flex items-center gap-x-1.6">
          <HourTypeDropdown
            value={selectedHourType}
            onChange={setSelectedHourType}
          />

          <AppointmentDateRange
            clinicIds={selectedClinicIds}
            dateRange={dateRangeForChildren}
            onChangeDateRange={onChangeDateRange}
            selectedCalendarType={selectedCalendarType}
            setSelectedCalendarType={setSelectedCalendarType}
          />
        </div>
      </div>

      {isWaitingForInitialRange ? (
        <div className="px-3.2 mt-2.4 flex justify-center items-center min-h-[24rem]">
          <LoadingSpinner className="all-child:fill-magenta w-3 h-3" />
        </div>
      ) : (
        <div className="px-3.2 mt-2.4 flex flex-col gap-y-2.4">
          <div className="flex gap-2.4">
            <div className="flex-[3]">
              <TotalProduction
                clinicIds={selectedClinicIds}
                selectedDateRange={dateRangeForChildren}
                hourType={selectedHourType.value}
              />
            </div>
            <div className="flex-1 min-w-[37rem]">
              <AppointmentsRequested
                clinicIds={selectedClinicIds}
                selectedDateRange={dateRangeForChildren}
                hourType={selectedHourType.value}
              />
            </div>
          </div>

          <div className="flex gap-2.4">
            <div className="flex-[3] flex gap-2.4">
              <StatCards
                clinicIds={selectedClinicIds}
                selectedDateRange={dateRangeForChildren}
                hourType={selectedHourType.value}
              />
            </div>
            <div className="flex-1 min-w-[37rem] min-h-[24.7rem]">
              <PatientsCard
                clinicIds={selectedClinicIds}
                selectedDateRange={dateRangeForChildren}
                hourType={selectedHourType.value}
              />
            </div>
          </div>

          <TrafficBySource
            clinicIds={selectedClinicIds}
            selectedDateRange={dateRangeForChildren}
            hourType={selectedHourType.value}
          />

          <PractitionerPerformance
            clinicIds={selectedClinicIds}
            selectedDateRange={dateRangeForChildren}
            hourType={selectedHourType.value}
          />

          <ServicePerformance
            clinicIds={selectedClinicIds}
            selectedDateRange={dateRangeForChildren}
            hourType={selectedHourType.value}
          />

          <RequestsScheduleOverTime
            clinicIds={selectedClinicIds}
            selectedDateRange={dateRangeForChildren}
            hourType={selectedHourType.value}
          />

          <TimeOfRequests
            clinicIds={selectedClinicIds}
            selectedDateRange={dateRangeForChildren}
            hourType={selectedHourType.value}
          />

          <div className="flex gap-2.4">
            <AgeOfPatients
              clinicIds={selectedClinicIds}
              selectedDateRange={dateRangeForChildren}
              hourType={selectedHourType.value}
            />
            <PreferredPractitioner
              clinicIds={selectedClinicIds}
              selectedDateRange={dateRangeForChildren}
              hourType={selectedHourType.value}
            />
          </div>

          <AppointmentMetricBreakdown
            clinicIds={selectedClinicIds}
            selectedDateRange={dateRangeForChildren}
            hourType={selectedHourType.value}
          />
        </div>
      )}
    </div>
  );
};

export default OnlineSchedulingReportPage;
